import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

/**
 * Notification types for snackbar
 */
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

/**
 * Configuration for notifications
 */
export interface NotificationConfig extends MatSnackBarConfig {
  type?: NotificationType;
}

/**
 * Centralized notification service for consistent snackbar styling across the application
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  // Default durations for different notification types
  private readonly DEFAULT_DURATION = 5000;

  // Emoji icons for different types
  private readonly ICONS = {
    success: '✅',
    error: '❌',
    info: 'ℹ️',
    warning: '⚠️'
  };

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Show a success notification
   */
  success(message: string, action: string = 'Cerrar', config?: NotificationConfig): void {
    this.show(message, 'success', action, config);
  }

  /**
   * Show an error notification
   */
  error(message: string, action: string = 'Cerrar', config?: NotificationConfig): void {
    this.show(message, 'error', action, config);
  }

  /**
   * Show an info notification
   */
  info(message: string, action: string = 'Cerrar', config?: NotificationConfig): void {
    this.show(message, 'info', action, config);
  }

  /**
   * Show a warning notification
   */
  warning(message: string, action: string = 'Cerrar', config?: NotificationConfig): void {
    this.show(message, 'warning', action, config);
  }

  /**
   * Show a generic notification with specified type
   */
  private show(
    message: string,
    type: NotificationType = 'info',
    action: string = 'Cerrar',
    config?: NotificationConfig
  ): void {
    // Build the full message with icon
    const icon = this.ICONS[type];
    const fullMessage = `${icon} ${message}`;

    // Merge with user config, but preserve essential properties
    const snackBarConfig: MatSnackBarConfig = {
      ...{
        duration: this.getDurationForType(type),
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      },
      ...config
    };

    this.snackBar.open(fullMessage, action, snackBarConfig);
  }

  /**
   * Get default duration for notification type
   */
  private getDurationForType(type: NotificationType): number | undefined {
    return type === 'info' || type === 'success' ? this.DEFAULT_DURATION : undefined;
  }

  /**
   * Dismiss all open snackbars
   */
  dismissAll(): void {
    this.snackBar.dismiss();
  }
}
